/* ==========================================================================
   Pack tiers — multi-buy quantity discounts (display layer)

   The prices shown here are a PREVIEW of what Shopify's automatic discounts
   will apply at cart/checkout. The theme never sets a price itself; every
   tier rendered must have a matching "Amount off products" automatic discount
   (percentage + minimum quantity) configured in the Shopify admin.

   Data sources
     - product cards / product page : inline <script type="application/json">
     - cart drawer                  : /products/<handle>?view=pack-tiers
   ========================================================================== */

(function () {
  'use strict';

  var CFG = window.packTiers || {};
  var tierCache = {};

  // Fallback copy lives here, not in a Liquid `default:` filter: Liquid cannot
  // lex a { } placeholder inside an inline filter argument.
  var DEFAULT_STRINGS = {
    add: 'ADD',
    percentOff: '{percent}% OFF',
    save: 'Save {amount}',
    summary: 'Buying {quantity} saves you {amount}',
    nudge: 'Add {count} more to save {amount}',
    nudgeCta: 'Make it {quantity}',
    addedToCart: 'Added {quantity} to your cart',
    addError: 'Could not add to cart',
    soldOut: 'Sold out',
    notEnoughStock: 'Only {count} left',
    discountStackNote: "Discount codes and pack savings don't combine \u2014 you'll get whichever is larger.",
    upgradeTitle: "You're one of our valued customers",
    upgradeBody: "We'll upgrade you to the {title} \u2014 for the same {price} you're already paying. That's {saving} on us.",
    upgradeAccept: 'Yes, upgrade me',
    upgradeDecline: 'No thanks',
    upgradeWorking: 'Applying your upgrade\u2026',
    upgradeApplied: 'Free size upgrade',
    upgradePropertyValue: 'Yes',
    upgradeLockedHint: 'Your free upgrade is limited to 1',
    upgradeAlreadyHave: "You've already got a free upgrade on this order.",
    deliveryLabel: 'Delivery',
    deliveryFree: 'FREE',
    deliveryTotalLabel: 'Total to pay',
    upgradeValueLabel: 'Free upgrade value',
    deliveryNote: 'Final delivery cost confirmed at checkout.',
    upgradeLineNote: 'Free upgrade \u2014 you will receive the {to} instead, at no extra cost.',
    upgradeDoneTitle: '\ud83c\udf89 Surprise \u2014 you have been upgraded!',
    upgradeDoneBody: "You ordered the {from}. We'll send you the {to} instead, at no extra cost \u2014 {saving} on us.",
    chooseOption: 'Option'
  };

  var STR = (function () {
    var merged = {};
    var supplied = CFG.strings || {};
    for (var key in DEFAULT_STRINGS) {
      if (!Object.prototype.hasOwnProperty.call(DEFAULT_STRINGS, key)) continue;
      merged[key] = supplied[key] != null && supplied[key] !== '' ? supplied[key] : DEFAULT_STRINGS[key];
    }
    return merged;
  })();

  /* ---------------------------------------------------------------- utils */

  function money(cents) {
    var value = Math.round(cents);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, CFG.moneyFormat);
    }
    return (value / 100).toFixed(2);
  }

  function fill(template, values) {
    return String(template || '').replace(/\{(\w+)\}/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  // "6:22|12:30" -> [{qty: 6, percent: 22}, {qty: 12, percent: 30}]
  function parseSpec(spec) {
    if (!spec) return [];
    var seen = {};
    return String(spec)
      .split('|')
      .map(function (chunk) {
        var parts = chunk.split(':');
        var qty = parseInt(parts[0], 10);
        var percent = parseFloat(parts[1]);
        if (!qty || qty < 2) return null;
        if (!(percent > 0) || percent >= 100) return null;
        if (seen[qty]) return null;
        seen[qty] = true;
        return { qty: qty, percent: percent };
      })
      .filter(Boolean)
      .sort(function (a, b) {
        return a.qty - b.qty;
      });
  }

  function pricing(unitPrice, tier) {
    var compare = unitPrice * tier.qty;
    var total = Math.round((compare * (100 - tier.percent)) / 100);
    return { compare: compare, total: total, save: compare - total };
  }

  function readJSON(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* --------------------------------------------------------- option rows */

  function buildMedia(qty, imageUrl, percent) {
    var media = el('div', 'pack-option__media' + (qty > 1 ? ' pack-option__media--stack' : ''));
    var copies = qty > 1 ? 3 : 1;

    for (var i = 0; i < copies; i++) {
      var img = document.createElement('img');
      img.src = imageUrl || '';
      img.alt = '';
      img.loading = 'lazy';
      media.appendChild(img);
    }

    if (percent) {
      media.appendChild(el('span', 'pack-option__badge', fill(STR.percentOff, { percent: Math.round(percent) })));
    }
    if (qty > 1) {
      media.appendChild(el('span', 'pack-option__count', 'x' + qty));
    }
    return media;
  }

  /**
   * data  : { title, image, variantTitle, unitPrice }
   * tier  : { qty, percent } or null for the single-unit row
   * mode  : 'add' (modal) | 'select' (product page)
   */
  function buildOption(data, tier, mode) {
    var qty = tier ? tier.qty : 1;
    var node = document.createElement(mode === 'select' ? 'button' : 'div');
    node.className = 'pack-option';
    node.setAttribute('data-pack-qty', String(qty));
    if (tier) node.setAttribute('data-pack-percent', String(tier.percent));

    if (mode === 'select') {
      node.type = 'button';
      node.setAttribute('aria-pressed', 'false');
    }

    node.appendChild(buildMedia(qty, data.image, tier ? tier.percent : 0));

    var meta = el('div', 'pack-option__meta');
    if (data.variantTitle) meta.appendChild(el('span', 'pack-option__variant', data.variantTitle));
    meta.appendChild(el('span', 'pack-option__qty', 'x ' + qty));
    node.appendChild(meta);

    var pricingBox = el('div', 'pack-option__pricing');
    var row = el('div', 'pack-option__price-row');

    if (tier) {
      var p = pricing(data.unitPrice, tier);
      row.appendChild(el('span', 'pack-option__price', money(p.total)));
      var compare = document.createElement('s');
      compare.className = 'pack-option__compare';
      compare.textContent = money(p.compare);
      row.appendChild(compare);
      pricingBox.appendChild(row);
      pricingBox.appendChild(el('span', 'pack-option__save', fill(STR.save, { amount: money(p.save) })));
    } else {
      row.appendChild(el('span', 'pack-option__price', money(data.unitPrice)));
      pricingBox.appendChild(row);
    }
    node.appendChild(pricingBox);

    // a tier stock cannot fill would add a partial quantity, landing the
    // shopper below the discount minimum at full price — block it instead
    var blocked = data.limit != null && qty > data.limit;
    if (blocked) {
      node.classList.add('pack-option--unavailable');
      pricingBox.appendChild(el('span', 'pack-option__stock', fill(STR.notEnoughStock, { count: data.limit })));
    }

    var action = el('div', 'pack-option__action');
    if (mode === 'select') {
      action.appendChild(el('span', 'pack-tiers__radio'));
    } else {
      var button = el('button', 'pack-option__add', STR.add);
      button.type = 'button';
      button.setAttribute('data-pack-add', String(qty));
      if (blocked) button.disabled = true;
      action.appendChild(button);
    }
    node.appendChild(action);

    if (blocked && mode === 'select') {
      node.disabled = true;
    }

    return node;
  }

  /* ------------------------------------------------------------ cart ops */

  function addToCart(variantId, quantity, properties) {
    var line = { id: variantId, quantity: quantity };
    if (properties) line.properties = properties;

    return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [line] })
    }).then(function (response) {
      // a non-JSON body (CDN error page, rate limit) must not leak a parse
      // error into the shopper-facing status line
      return response.text().then(function (text) {
        var body = null;
        try {
          body = JSON.parse(text);
        } catch (e) {
          body = null;
        }
        if (!response.ok) {
          // /cart/add.js answers with {description, message}; /cart/add — which
          // is what routes.cart_add_url points at — answers with {errors},
          // either a string or a field -> messages object
          var reason = '';
          if (body) {
            reason = body.description || body.message || '';
            if (!reason && body.errors) {
              reason = typeof body.errors === 'string'
                ? body.errors
                : Object.keys(body.errors).map(function (k) {
                    return [].concat(body.errors[k]).join(', ');
                  }).join(' ');
            }
          }
          throw new Error(reason);
        }
        return body;
      });
    });
  }

  function refreshCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var nodes = document.querySelectorAll('#CartCount, .cart-count-bubble span, [data-cart-count]');
        for (var i = 0; i < nodes.length; i++) nodes[i].textContent = cart.item_count;
        return cart;
      })
      .catch(function () { /* count is cosmetic — never block the add */ });
  }

  function reloadCartDrawer() {
    if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
      window.ajaxCart.load();
    } else {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }
  }


  /**
   * The cart drawer installs a jQuery focus trap (mono.js Drawer.trapFocus):
   *   $(document).on('focusin.drawer_focus', ... $container.focus() ...)
   * which yanks focus back to #CartDrawer whenever anything outside it gains
   * focus. A dialog rendered outside the drawer therefore cannot be typed in —
   * clicks land, focus is stolen, and the field looks disabled.
   * Release the trap while the dialog is open, restore it afterwards.
   */
  function releaseDrawerFocusTrap() {
    if (window.jQuery) {
      try { window.jQuery(document).off('focusin.drawer_focus'); } catch (e) { /* noop */ }
    }
  }

  function restoreDrawerFocusTrap() {
    if (!window.jQuery) return;
    if (!document.body.classList.contains('js-drawer-open')) return;
    var drawer = window.jQuery('#CartDrawer');
    if (!drawer.length) return;
    if (window.mono && window.mono.RightDrawer && typeof window.mono.RightDrawer.trapFocus === 'function') {
      try { window.mono.RightDrawer.trapFocus(drawer, 'drawer_focus'); } catch (e) { /* noop */ }
    }
  }

  /* -------------------------------------------------------------- modal */

  var Modal = {
    root: null,
    data: null,
    added: false,

    init: function () {
      this.root = document.getElementById('PackTiersModal');
      if (!this.root) return;

      var self = this;

      this.root.addEventListener('click', function (event) {
        if (event.target.closest('[data-pack-close]')) {
          event.preventDefault();
          self.close();
          return;
        }
        var addButton = event.target.closest('[data-pack-add]');
        if (addButton) {
          event.preventDefault();
          self.add(addButton);
        }
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && !self.root.hidden) self.close();
      });

      document.addEventListener('click', function (event) {
        var trigger = event.target.closest('[data-pack-open]');
        if (!trigger) return;
        var source = document.getElementById(trigger.getAttribute('data-pack-open'));
        var payload = readJSON(source);
        if (!payload) return;
        event.preventDefault();
        self.open(payload);
      });
    },

    open: function (payload) {
      this.data = payload;
      this.added = false;
      this.root.querySelector('.pack-modal__title').textContent = payload.title || '';
      this.setStatus('');
      this.renderVariants();
      this.renderOptions();
      releaseDrawerFocusTrap();
      this.root.hidden = false;
      document.body.classList.add('pack-modal-open');
      this.root.querySelector('.pack-modal__close').focus();
    },

    close: function () {
      this.root.hidden = true;
      document.body.classList.remove('pack-modal-open');
      restoreDrawerFocusTrap();
      if (this.added) reloadCartDrawer();
      this.added = false;
    },

    setStatus: function (text) {
      var node = this.root.querySelector('.pack-modal__status');
      node.textContent = text || '';
      node.classList.remove('pack-modal__status--error');
    },

    currentVariant: function () {
      var variants = (this.data && this.data.variants) || [];
      var select = this.root.querySelector('.pack-modal__variant select');
      if (select && select.value) {
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === select.value) return variants[i];
        }
      }
      for (var j = 0; j < variants.length; j++) {
        if (variants[j].available) return variants[j];
      }
      return variants[0] || null;
    },

    renderVariants: function () {
      var wrapper = this.root.querySelector('.pack-modal__variant');
      var variants = (this.data && this.data.variants) || [];
      wrapper.innerHTML = '';

      if (variants.length < 2) {
        wrapper.hidden = true;
        return;
      }

      var label = el('label', null, STR.chooseOption || 'Option');
      label.setAttribute('for', 'PackTiersVariant');
      var select = document.createElement('select');
      select.id = 'PackTiersVariant';

      variants.forEach(function (variant) {
        var option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.available
          ? variant.title
          : variant.title + ' — ' + (STR.soldOut || 'Sold out');
        option.disabled = !variant.available;
        select.appendChild(option);
      });

      var self = this;
      select.addEventListener('change', function () {
        self.renderOptions();
      });

      wrapper.appendChild(label);
      wrapper.appendChild(select);
      wrapper.hidden = false;
    },

    renderOptions: function () {
      var container = this.root.querySelector('.pack-modal__options');
      container.innerHTML = '';

      var variant = this.currentVariant();
      if (!variant) return;

      var base = {
        image: variant.image || this.data.image,
        variantTitle: variant.title && variant.title !== 'Default Title' ? variant.title : '',
        unitPrice: variant.price,
        limit: variant.limit == null ? null : variant.limit
      };
      var tiers = parseSpec(this.data.spec);

      container.appendChild(buildOption(base, null, 'add'));
      tiers.forEach(function (tier) {
        container.appendChild(buildOption(base, tier, 'add'));
      });

      if (!variant.available) {
        container.querySelectorAll('[data-pack-add]').forEach(function (button) {
          button.disabled = true;
          button.textContent = STR.soldOut || 'Sold out';
        });
      }
    },

    add: function (button) {
      var variant = this.currentVariant();
      if (!variant || button.disabled) return;

      var quantity = parseInt(button.getAttribute('data-pack-add'), 10) || 1;
      var self = this;

      button.classList.add('is-loading');
      button.disabled = true;

      addToCart(variant.id, quantity)
        .then(function () {
          self.added = true;
          self.setStatus(fill(STR.addedToCart, { quantity: quantity }));
          return refreshCartCount();
        })
        .catch(function (error) {
          self.setStatus(error.message || STR.addError || 'Could not add to cart');
          self.root.querySelector('.pack-modal__status').classList.add('pack-modal__status--error');
        })
        .finally(function () {
          button.classList.remove('is-loading');
          button.disabled = false;
        });
    }
  };

  /* ------------------------------------------------- product page picker */

  function initProductPickers(scope) {
    var pickers = (scope || document).querySelectorAll('.pack-tiers:not([data-pack-ready])');

    Array.prototype.forEach.call(pickers, function (picker) {
      picker.setAttribute('data-pack-ready', 'true');

      var payload = readJSON(document.getElementById(picker.getAttribute('data-pack-source')));
      if (!payload) return;

      var list = picker.querySelector('.pack-tiers__list');
      var summary = picker.querySelector('.pack-tiers__summary');

      function variantById(id) {
        var variants = payload.variants || [];
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(id)) return variants[i];
        }
        return variants[0] || null;
      }

      function selectedVariant() {
        var form = picker.closest('.product-section, .qu_product, form') || document;
        var input = form.querySelector('input[name="id"], select[name="id"]');
        return variantById(input && input.value);
      }

      function quantityInput() {
        var container = picker.closest('.product-section, .qu_product') || document;
        return container.querySelector('input[name="quantity"]');
      }

      function render() {
        var variant = selectedVariant();
        if (!variant) return;

        var base = {
          image: variant.image || payload.image,
          variantTitle: variant.title && variant.title !== 'Default Title' ? variant.title : '',
          unitPrice: variant.price,
          limit: variant.limit == null ? null : variant.limit
        };
        var tiers = parseSpec(payload.spec);

        list.innerHTML = '';
        list.appendChild(buildOption(base, null, 'select'));
        tiers.forEach(function (tier) {
          list.appendChild(buildOption(base, tier, 'select'));
        });

        var input = quantityInput();
        select(input ? parseInt(input.value, 10) || 1 : 1, false);
      }

      function select(quantity, updateInput) {
        var options = list.querySelectorAll('.pack-option');
        var chosen = null;

        Array.prototype.forEach.call(options, function (option) {
          var optionQty = parseInt(option.getAttribute('data-pack-qty'), 10);
          var isMatch = optionQty === quantity;
          option.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
          if (isMatch) chosen = option;
        });

        if (updateInput) {
          var input = quantityInput();
          if (input) {
            input.value = quantity;
            input.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        if (!summary) return;
        var percent = chosen && parseFloat(chosen.getAttribute('data-pack-percent'));
        if (chosen && percent > 0) {
          var variant = selectedVariant();
          var p = pricing(variant.price, { qty: quantity, percent: percent });
          summary.textContent = fill(STR.summary, {
            quantity: quantity,
            amount: money(p.save),
            percent: Math.round(percent)
          });
        } else {
          summary.textContent = '';
        }
      }

      list.addEventListener('click', function (event) {
        var option = event.target.closest('.pack-option');
        if (!option || option.disabled) return;
        select(parseInt(option.getAttribute('data-pack-qty'), 10) || 1, true);
      });

      // keep in sync with the theme's variant picker and quantity stepper
      var host = picker.closest('.product-section, .qu_product') || document;
      host.addEventListener('change', function (event) {
        var target = event.target;
        if (!target.name) return;
        if (target.name === 'id') render();
        if (target.name === 'quantity') select(parseInt(target.value, 10) || 1, false);
      });
      host.addEventListener('click', function (event) {
        var stepper = event.target.closest('.quantity__button');
        if (!stepper) return;
        setTimeout(function () {
          var input = quantityInput();
          if (input) select(parseInt(input.value, 10) || 1, false);
        }, 0);
      });

      render();
    });
  }

  /* --------------------------------------------------- cart drawer nudge */

  function fetchLineData(handle) {
    if (Object.prototype.hasOwnProperty.call(tierCache, handle)) {
      return Promise.resolve(tierCache[handle]);
    }
    // NO Accept: application/json here. Shopify content-negotiates product URLs:
    // with that header it returns its own {"product": {...}} payload and ignores
    // ?view= entirely, so the alternate template never runs.
    return fetch('/products/' + handle + '?view=line-data')
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (payload) {
        // only cache a real answer — caching a network failure would keep the
        // offer hidden for the rest of the session
        if (payload) tierCache[handle] = payload;
        return payload;
      })
      .catch(function () {
        return null;
      });
  }

  /* ------------------------------------------------- free size upgrade */

  function upgradeDismissed(handle) {
    try {
      return window.sessionStorage.getItem('packUpgradeDismissed:' + handle) === '1';
    } catch (e) {
      return false;
    }
  }

  function dismissUpgrade(handle) {
    try {
      window.sessionStorage.setItem('packUpgradeDismissed:' + handle, '1');
    } catch (e) {
      /* private browsing — the offer simply reappears next render */
    }
  }

  /**
   * Swap the smaller line for the bigger product, then hand off to Shopify's
   * /discount endpoint so the code that covers the price gap is applied.
   * The handoff is a navigation on purpose: /discount can redirect across
   * domains, which a background fetch cannot follow.
   */
  function acceptUpgrade(item, line, quantity, upgrade, button) {
    button.disabled = true;
    button.textContent = STR.upgradeWorking;

    var changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';

    // The gift is ONE item, never the whole line.
    var keepBehind = Math.max(0, quantity - 1);

    // No discount code is used. The shopper keeps the product they chose, at the
    // price they chose, and a visible line-item property tells us to ship the
    // bigger size. Shopify allows one code per order, so a code-based upgrade
    // was silently evicted whenever the shopper entered ROUGE15 at checkout.
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (live) {
        if (cartHasUpgrade(live)) {
          markUpgradeClaimed();
          button.textContent = STR.upgradeAlreadyHave;
          var card = button.closest('.pack-upgrade');
          if (card) window.setTimeout(function () { card.remove(); }, 2500);
          throw new Error('__already_upgraded__');
        }
        return fetch(changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: line, quantity: keepBehind })
        });
      })
      .then(function () {
        var props = {};
        props[UPGRADE_PROP] = upgrade.to.title;
        // same variant the shopper picked — only the flag is new
        return addToCart(item.variant_id, 1, props);
      })
      .then(function () {
        try {
          window.sessionStorage.setItem(
            'packUpgradeDone:' + item.handle,
            JSON.stringify({
              from: item.product_title,
              to: upgrade.to.title,
              saving: upgrade.saving
            })
          );
        } catch (e) {
          /* private browsing — the shopper just misses the confirmation */
        }
        markUpgradeClaimed();
        reloadCartDrawer();
      })
      .catch(function (error) {
        if (error && error.message === '__already_upgraded__') return;
        button.disabled = false;
        button.textContent = STR.upgradeAccept;
        reloadCartDrawer();
        if (window.console) window.console.error('[pack-tiers] upgrade failed', error);
      });
  }

  /**
   * Shopify shows a code discount using its title, and for a discount code the
   * title IS the code — so the shopper sees "9WAHQKKB8G5Q" in their cart. These
   * codes are deliberately random (they are visible in the apply URL), so they
   * can never be friendly. Swap the rendered text for a human label instead.
   */
  function upgradeCodes() {
    return String(CFG.upgradeMap || '')
      .split(/[\r\n]+/)
      .map(function (line) {
        var parts = line.split(':');
        return parts.length > 1 ? parts[1].trim().toUpperCase() : '';
      })
      .filter(Boolean);
  }

  /**
   * By Shopify convention a line-item property whose key starts with "_" is
   * internal and must not be shown to the shopper. This theme's cart template
   * prints every property, so hide those here — for our upgrade flag and for
   * any app that relies on the same convention.
   */
  function hideInternalProperties() {
    var nodes = document.querySelectorAll('.ajaxcart__product-meta, .product-option, .cart-item__meta span');
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.children.length) return;
      if ((node.textContent || '').trim().charAt(0) === '_') {
        node.hidden = true;
        node.style.display = 'none';
      }
    });
  }

  /**
   * Hide named discount labels on cart lines. The strikethrough and the reduced
   * price stay — only the code name goes, so the shopper still sees they are
   * paying less without a code string they may not recognise.
   * Other discounts (e.g. "Buy 6, save 22%") are left alone: those explain
   * themselves and are worth showing.
   */
  function hideNamedDiscountLabels() {
    var raw = String(CFG.hideDiscountLabels || '').trim();
    if (!raw) return;
    var names = raw.split(',').map(function (n) { return n.trim().toUpperCase(); }).filter(Boolean);
    if (!names.length) return;

    var nodes = document.querySelectorAll('.ajaxcart-item__discount, .discounts__discount');
    Array.prototype.forEach.call(nodes, function (node) {
      var text = (node.textContent || '').trim().toUpperCase();
      if (names.indexOf(text) === -1) return;
      node.hidden = true;
      node.style.display = 'none';
    });
  }

  function relabelUpgradeDiscounts() {
    var codes = upgradeCodes();
    if (!codes.length || !STR.upgradeApplied) return;

    var nodes = document.querySelectorAll(
      '.ajaxcart-item__discount span, .ajaxcart-item__discount, [class*="discount"] span'
    );
    Array.prototype.forEach.call(nodes, function (node) {
      if (node.children.length) return;
      var text = (node.textContent || '').trim().toUpperCase();
      if (codes.indexOf(text) === -1) return;
      node.textContent = STR.upgradeApplied;
      node.setAttribute('data-pack-relabelled', 'true');
    });
  }

  var CLAIM_KEY = 'packUpgradeClaimed';
  // Visible on purpose. It shows in the cart, at Shopify's checkout and on the
  // order, so the shopper can see what they are getting and the packing team
  // knows what to send. No discount code is involved any more.
  var UPGRADE_PROP = 'Free size upgrade';

  function upgradeClaimed() {
    if (CFG.upgradeOncePerCustomer === false) return false;
    try {
      return !!window.localStorage.getItem(CLAIM_KEY);
    } catch (e) {
      return false;
    }
  }

  function markUpgradeClaimed() {
    try {
      window.localStorage.setItem(CLAIM_KEY, String(Date.now()));
    } catch (e) {
      /* storage blocked — the cart check below still holds the rule */
    }
  }

  function forgetUpgradeClaimed() {
    try {
      window.localStorage.removeItem(CLAIM_KEY);
    } catch (e) {
      /* nothing to clear */
    }
  }

  /** an upgrade already sitting in this cart also counts as claimed */
  function cartHasUpgrade(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (item) {
      return !!(item.properties || {})[UPGRADE_PROP];
    });
  }

  function readUpgradeDone(handle) {
    try {
      var raw = window.sessionStorage.getItem('packUpgradeDone:' + handle);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function forgetUpgradeDone(handle) {
    try {
      window.sessionStorage.removeItem('packUpgradeDone:' + handle);
    } catch (e) {
      /* nothing to clean up */
    }
  }

  /**
   * The upgrade redirects to /cart, where this theme deliberately omits the
   * cart drawer — so the confirmation cannot live inside the drawer markup.
   * Render it as a banner at the top of whichever cart container exists.
   */
  function confirmationHost() {
    // #CartContainer is display:flex/row, so a banner inserted there becomes a
    // flex item sitting BESIDE the cart form. Target the block-flow wrapper.
    return (
      document.querySelector('#CartContainer .ajaxcart__inner') ||
      document.getElementById('main-cart-items') ||
      document.querySelector('.cart__items, cart-items, form#cart') ||
      document.getElementById('CartContainer')
    );
  }

  function renderUpgradeConfirmations(cart) {
    var host = confirmationHost();
    if (!host) return;

    var existing = host.querySelector(':scope > .pack-upgrade-done');
    var codes = upgradeCodes();
    var records = [];
    (cart && cart.items ? cart.items : []).forEach(function (item) {
      var record = readUpgradeDone(item.handle);
      if (!record) return;
      // celebrate only while the flagged line is genuinely still in the cart
      if ((item.properties || {})[UPGRADE_PROP]) {
        records.push(record);
      } else {
        forgetUpgradeDone(item.handle);
      }
    });

    if (!records.length) {
      if (existing) existing.remove();
      return;
    }
    if (existing) return;

    host.insertBefore(buildUpgradeDone(records[0]), host.firstChild);
  }

  /**
   * When a shopper raises the quantity on the gifted line, Shopify spreads the
   * single discount across every unit (3 x 365 less 100 shows as 331.67 each)
   * and the discount badge reads as if all of them were upgraded. Spell out
   * what actually happened on that line.
   */
  /**
   * The gift is exactly one item, so freeze the quantity on that line. Without
   * this a shopper can raise it and Shopify averages the single discount across
   * every unit, which reads as though all of them were upgraded.
   * Removing the line stays possible — only the steppers are locked.
   */
  function lockUpgradeQuantity(cart) {
    if (!cart || !cart.items) return;

    cart.items.forEach(function (item, index) {
      if (!(item.properties || {})[UPGRADE_PROP]) return;

      var row =
        document.querySelector('#CartContainer .ajaxcart__row[data-line="' + (index + 1) + '"]') ||
        document.getElementById('CartItem-' + (index + 1));
      if (!row) return;

      var hint = STR.upgradeLockedHint || '';
      var buttons = row.querySelectorAll(
        '.ajaxcart__qty-adjust, .quantity__button, button[name="minus"], button[name="plus"]'
      );
      Array.prototype.forEach.call(buttons, function (button) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('pack-qty-locked');
        if (hint) button.title = hint;
      });

      var inputs = row.querySelectorAll('.ajaxcart__qty-num, .quantity__input, input[name="updates[]"]');
      Array.prototype.forEach.call(inputs, function (input) {
        input.readOnly = true;
        input.setAttribute('aria-readonly', 'true');
        input.classList.add('pack-qty-locked');
        if (hint) input.title = hint;
      });
    });
  }

  function annotateUpgradeLines(cart) {
    if (!cart || !cart.items) return;

    cart.items.forEach(function (item, index) {
      var target = (item.properties || {})[UPGRADE_PROP];
      if (!target) return;

      var row =
        document.querySelector('#CartContainer .ajaxcart__row[data-line="' + (index + 1) + '"]') ||
        document.getElementById('CartItem-' + (index + 1));
      if (!row || row.querySelector('.pack-upgrade-note')) return;

      var text = fill(STR.upgradeLineNote, { to: target });
      var cell =
        row.querySelector('.cart-item__details') ||
        (row.querySelector('.cart-item__name') && row.querySelector('.cart-item__name').parentElement) ||
        row.querySelector('td:nth-child(2)') ||
        row;
      cell.appendChild(el('div', 'pack-upgrade-note', text));
    });
  }

  function buildUpgradeDone(record) {
    var card = el('div', 'pack-upgrade-done');
    card.appendChild(el('strong', 'pack-upgrade-done__title', STR.upgradeDoneTitle));
    card.appendChild(el('p', 'pack-upgrade-done__text', fill(STR.upgradeDoneBody, {
      from: record.from,
      to: record.to,
      saving: money(record.saving)
    })));
    return card;
  }

  function buildUpgradeOffer(item, line, upgrade) {
    var card = el('div', 'pack-upgrade');

    var media = el('div', 'pack-upgrade__media');
    var img = document.createElement('img');
    img.src = upgrade.to.image || '';
    img.alt = '';
    img.loading = 'lazy';
    media.appendChild(img);
    card.appendChild(media);

    // Quote what this line ACTUALLY costs, not the product's list price. A
    // coupon such as ROUGE15 discounts the line, so "the same Dhs. 280 you're
    // already paying" would be wrong when the shopper is really paying 238.
    var payingNow = item.discounted_price;
    if (payingNow == null) payingNow = item.final_price;
    if (payingNow == null) payingNow = item.price;
    if (payingNow == null) payingNow = upgrade.from_price;

    var body = el('div', 'pack-upgrade__body');
    body.appendChild(el('strong', 'pack-upgrade__title', STR.upgradeTitle));
    body.appendChild(el('p', 'pack-upgrade__text', fill(STR.upgradeBody, {
      title: upgrade.to.title,
      price: money(payingNow),
      saving: money(upgrade.saving)
    })));

    var actions = el('div', 'pack-upgrade__actions');
    var accept = el('button', 'pack-upgrade__accept', STR.upgradeAccept);
    accept.type = 'button';
    accept.addEventListener('click', function () {
      acceptUpgrade(item, line, item.quantity, upgrade, accept);
    });

    var decline = el('button', 'pack-upgrade__decline', STR.upgradeDecline);
    decline.type = 'button';
    decline.addEventListener('click', function () {
      dismissUpgrade(item.handle);
      card.remove();
    });

    actions.appendChild(accept);
    actions.appendChild(decline);
    body.appendChild(actions);
    card.appendChild(body);

    return card;
  }

  /**
   * The theme only PREVIEWS tier pricing; Shopify's automatic discount is what
   * actually charges. If the two disagree the shopper is quoted one price and
   * billed another, silently — so check every cart line and shout about it.
   */
  function auditLine(item, tiers) {
    var earned = null;
    for (var i = 0; i < tiers.length; i++) {
      if (item.quantity >= tiers[i].qty) earned = tiers[i];
    }
    if (!earned) return null;

    var expected = Math.round((item.original_line_price * earned.percent) / 100);
    var actual = item.total_discount || 0;
    if (Math.abs(expected - actual) <= 2) return null;

    return {
      title: item.product_title,
      quantity: item.quantity,
      percent: earned.percent,
      expected: expected,
      actual: actual
    };
  }

  function reportAudit(problem) {
    if (window.console && window.console.warn) {
      window.console.warn(
        '[pack-tiers] "' + problem.title + '" x' + problem.quantity +
        ' should get ' + problem.percent + '% off (' + money(problem.expected) +
        ') but the cart gave ' + money(problem.actual) + '.' +
        ' Create an "Amount off products" AUTOMATIC discount in Shopify admin:' +
        ' ' + problem.percent + '% off, minimum quantity ' + problem.quantity + '.' +
        ' Until then the shopper is quoted a price they will not be charged.'
      );
    }
    if (!CFG.debug) return;
    var banner = document.querySelector('.pack-audit');
    if (!banner) {
      banner = el('div', 'pack-audit');
      var host = document.getElementById('CartContainer');
      if (host) host.insertBefore(banner, host.firstChild);
    }
    banner.textContent =
      'Preview mismatch: ' + problem.title + ' x' + problem.quantity + ' shows ' +
      money(problem.expected) + ' off but the cart applied ' + money(problem.actual) +
      '. The matching automatic discount is missing in Shopify admin.';
  }

  function nextTier(tiers, quantity) {
    for (var i = 0; i < tiers.length; i++) {
      if (tiers[i].qty > quantity) return tiers[i];
    }
    return null;
  }

  function changeLine(line, quantity) {
    if (window.ShopifyAPI && typeof window.ShopifyAPI.changeItem === 'function') {
      window.ShopifyAPI.changeItem(line, quantity, function () {});
      return;
    }
    fetch((window.routes && window.routes.cart_change_url) || '/cart/change', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    }).then(reloadCartDrawer);
  }

  /**
   * Shopify applies whichever discount saves the shopper more; a code can
   * supersede a pack saving (or be refused when the pack saving is bigger).
   * Say so in the cart, so "where did my 10% go?" never comes up at checkout.
   */
  function showDiscountStackNote() {
    if (!STR.discountStackNote) return;

    var host =
      document.querySelector('#CartContainer .ajaxcart__footer') ||
      (document.querySelector('.totals') && document.querySelector('.totals').parentElement) ||
      document.querySelector('.cart__blocks');
    if (!host || host.querySelector('.pack-stack-note')) return;

    var note = el('p', 'pack-stack-note', STR.discountStackNote);

    // .pack-totals is rendered later (it waits on the shipping-rate lookup), so
    // anchor on the checkout button, which is always in the markup. The totals
    // then insert between this note and the button, which is the order we want.
    var anchor =
      host.querySelector('.pack-totals') ||
      host.querySelector('.cart__checkout, .cart__checkout-button') ||
      host.querySelector('.totals');
    if (anchor && anchor.parentElement === host) host.insertBefore(note, anchor);
    else host.appendChild(note);
  }

  function renderCartExtras(cart) {
    hideInternalProperties();
    hideNamedDiscountLabels();
    relabelUpgradeDiscounts();
    renderUpgradeConfirmations(cart);
    annotateUpgradeLines(cart);
    lockUpgradeQuantity(cart);
    renderDeliveryTotals(cart);
    refreshFreeShippingMessage();
    if (!cart || !cart.items) return;

    // Keep stored state in step with the cart. A confirmation for a product the
    // shopper removed is stale, and a "No thanks" should not outlive the item
    // either — if they take it out and add it back, they have reconsidered and
    // should see the offer again.
    try {
      var present = cart.items.map(function (i) { return i.handle; });
      var prefixes = ['packUpgradeDone:', 'packUpgradeDismissed:'];
      for (var k = window.sessionStorage.length - 1; k >= 0; k--) {
        var key = window.sessionStorage.key(k);
        if (!key) continue;
        for (var pi = 0; pi < prefixes.length; pi++) {
          if (key.indexOf(prefixes[pi]) !== 0) continue;
          if (present.indexOf(key.slice(prefixes[pi].length)) === -1) {
            window.sessionStorage.removeItem(key);
          }
        }
      }
    } catch (e) {
      /* storage unavailable */
    }

    var threshold = CFG.upgradeThreshold || 0;
    // one upgrade per customer: never offer a second one, and never offer while
    // an upgraded item is already in the cart
    // Keep the claim flag in step with the cart. If the shopper removes the
    // upgraded item they no longer hold the gift, so the offer should come back
    // — a flag that never reset left them permanently locked out.
    var hasUpgradeNow = cartHasUpgrade(cart);
    if (hasUpgradeNow) markUpgradeClaimed();
    else forgetUpgradeClaimed();

    var alreadyUpgraded = hasUpgradeNow;
    var eligibleForUpgrade = CFG.showUpgrade && !alreadyUpgraded && cart.total_price >= threshold;

    cart.items.forEach(function (item, index) {
      var line = index + 1;
      var row = document.querySelector('#CartContainer .ajaxcart__row[data-line="' + line + '"]');
      if (!row) return;

      fetchLineData(item.handle).then(function (payload) {
        if (!payload) return;
        // append to the row itself: .drawer-price is a narrow, right-aligned
        // half-width table cell and squeezes the card to one word per line
        var anchor = row;

        // 1. free size upgrade offer
        // Only ONE offer at a time. The shopper gets a single upgrade, so
        // showing two at once implies they could claim both.
        if (
          eligibleForUpgrade &&
          payload.upgrade &&
          payload.upgrade.to &&
          !upgradeDismissed(item.handle) &&
          !document.querySelector('.pack-upgrade')
        ) {
          anchor.appendChild(buildUpgradeOffer(item, line, payload.upgrade));
        }

        if (!payload.spec) return;
        showDiscountStackNote();
        var tiers = parseSpec(payload.spec);

        // 2. does the cart actually honour the tier we advertised?
        var problem = auditLine(item, tiers);
        if (problem) reportAudit(problem);

        // 3. "add N more to save" tier nudge
        if (!CFG.showCartNudge || row.querySelector('.pack-nudge')) return;

        var tier = nextTier(tiers, item.quantity);
        if (!tier) return;

        var unitPrice = item.original_price || item.price;
        var p = pricing(unitPrice, tier);

        var nudge = el('div', 'pack-nudge');
        nudge.appendChild(el('span', 'pack-nudge__text', fill(STR.nudge, {
          count: tier.qty - item.quantity,
          quantity: tier.qty,
          amount: money(p.save),
          percent: Math.round(tier.percent)
        })));

        var cta = el('button', 'pack-nudge__cta', fill(STR.nudgeCta, { quantity: tier.qty }));
        cta.type = 'button';
        cta.addEventListener('click', function () {
          cta.disabled = true;
          changeLine(line, tier.qty);
        });
        nudge.appendChild(cta);
        anchor.appendChild(nudge);
      });
    });
  }

  /* ------------------------------------------------- delivery + total */

  var shippingCache = {};

  function fetchDeliveryCost(cart) {
    var key = String(cart.total_price);
    if (Object.prototype.hasOwnProperty.call(shippingCache, key)) {
      return Promise.resolve(shippingCache[key]);
    }
    var query =
      '?shipping_address%5Bcountry%5D=' + encodeURIComponent(CFG.deliveryCountry || 'United Arab Emirates') +
      '&shipping_address%5Bprovince%5D=' + encodeURIComponent(CFG.deliveryProvince || '') +
      '&shipping_address%5Bzip%5D=00000';

    return fetch('/cart/shipping_rates.json' + query)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (body) {
        var rates = (body && body.shipping_rates) || [];
        if (!rates.length) return null;
        var cheapest = rates.reduce(function (lowest, rate) {
          var cents = Math.round(parseFloat(rate.price) * 100);
          return isNaN(cents) ? lowest : (lowest === null || cents < lowest ? cents : lowest);
        }, null);
        shippingCache[key] = cheapest;
        return cheapest;
      })
      .catch(function () { return null; });
  }

  /**
   * The upgrade is not money off — the shopper still pays the smaller size's
   * price — so it never appears in Shopify's savings total. Surface its value
   * in the cart so they can see what the gift is actually worth.
   */
  function upgradeValueInCart(cart) {
    if (!cart || !cart.items) return Promise.resolve(0);
    var flagged = cart.items.filter(function (item) {
      return !!(item.properties || {})[UPGRADE_PROP];
    });
    if (!flagged.length) return Promise.resolve(0);

    return Promise.all(flagged.map(function (item) {
      return fetchLineData(item.handle).then(function (payload) {
        return (payload && payload.upgrade && payload.upgrade.saving) || 0;
      });
    })).then(function (values) {
      return values.reduce(function (sum, v) { return sum + v; }, 0);
    }).catch(function () { return 0; });
  }

  function row(label, value, extraClass) {
    var line = el('div', 'pack-totals__row' + (extraClass ? ' ' + extraClass : ''));
    line.appendChild(el('span', 'pack-totals__label', label));
    line.appendChild(el('span', 'pack-totals__value', value));
    return line;
  }

  /**
   * The drawer only ever showed a subtotal and "shipping calculated at
   * checkout", so shoppers could not see what they would actually pay.
   * This store ships UAE-only on one flat rule, so an estimate is reliable.
   */
  /**
   * The theme's "you are X away from FREE SHIPPING" banner is calculated once
   * on page load and never again, so it goes stale the moment the cart changes
   * — and contradicts the delivery row we now show. Recalculate it.
   */
  function refreshFreeShippingMessage() {
    if (typeof window.updateFreeShippingThersold === 'function') {
      try {
        window.updateFreeShippingThersold();
      } catch (e) {
        /* the banner is cosmetic — never let it break the cart */
      }
    }
  }

  function renderDeliveryTotals(cart) {
    if (!CFG.showDelivery || !cart || !cart.items || !cart.items.length) return;

    // .totals is a flex ROW holding the subtotal label and value — inserting
    // here would squash all of them into columns. Use its block-level parent.
    var drawerFooter = document.querySelector('#CartContainer .ajaxcart__footer');
    var totals = document.querySelector('.totals');
    var host = drawerFooter || (totals && totals.parentElement) || document.querySelector('.cart__blocks');
    if (!host) return;

    Promise.all([fetchDeliveryCost(cart), upgradeValueInCart(cart)]).then(function (results) {
      var shipping = results[0];
      var upgradeValue = results[1];
      if (shipping === null || shipping === undefined) return;
      if (host.querySelector('.pack-totals')) return;

      var box = el('div', 'pack-totals');
      if (upgradeValue > 0) {
        box.appendChild(row(STR.upgradeValueLabel, money(upgradeValue), 'pack-totals__row--free'));
      }
      box.appendChild(row(STR.deliveryLabel, shipping === 0 ? STR.deliveryFree : money(shipping),
        shipping === 0 ? 'pack-totals__row--free' : ''));
      box.appendChild(row(STR.deliveryTotalLabel, money(cart.total_price + shipping), 'pack-totals__row--total'));
      if (STR.deliveryNote) box.appendChild(el('p', 'pack-totals__note', STR.deliveryNote));

      // "shipping calculated at checkout" contradicts the delivery row we just
      // added, so retire it — but only when our totals actually rendered, so
      // turning the feature off restores the theme's original note.
      // the theme's own "You Save" row duplicates what our totals already show
      var savedRow = document.querySelectorAll('#CartContainer .row.discount, .ajaxcart__savings');
      Array.prototype.forEach.call(savedRow, function (node) {
        var box = node.closest('.row') || node;
        box.hidden = true;
        box.style.display = 'none';
      });

      var taxNote = document.querySelectorAll('.tax-note');
      Array.prototype.forEach.call(taxNote, function (node) {
        node.hidden = true;
        node.style.display = 'none';
      });

      var checkout = host.querySelector('.cart__checkout, .cart__checkout-button');
      if (!checkout && totals && totals.parentElement === host) {
        // cart page: sit directly under the subtotal row
        host.insertBefore(box, totals.nextSibling);
      } else if (checkout) {
        host.insertBefore(box, checkout);
        // the button advertised the subtotal, which contradicted the new total
        var span = checkout.querySelector('span') || checkout;
        if (/~/.test(span.textContent)) {
          span.textContent = span.textContent.split('~')[0].trim() + ' ~ ' + money(cart.total_price + shipping);
        }
      } else {
        host.appendChild(box);
      }
    });
  }

  /* ------------------------------------------ stale discount code cleanup */

  /**
   * Shopify keeps a discount code on the SESSION, not the cart. A shopper who
   * enters ROUGE15 at checkout, abandons payment and carries on browsing then
   * sees every product discounted — a price they may not be entitled to, since
   * the code is one-use-per-customer. Clearing the cart does not remove it.
   *
   * Dropping the code whenever they are back on the storefront keeps prices
   * honest; they can re-enter it at checkout when they are ready to pay.
   * Verified: this clears CODES only — automatic discounts such as
   * "Buy 6, save 22%" are untouched.
   */
  function clearStaleDiscountCode() {
    if (!CFG.clearDiscountOnReturn) return;
    if (window.__packDiscountCodeCleared) return;
    window.__packDiscountCodeCleared = true;

    fetch((window.routes && window.routes.cart_update_url) || '/cart/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: '' })
    }).catch(function () {
      /* pricing simply stays as Shopify had it */
    });
  }

  /* --------------------------------------------------------------- boot */

  function boot() {
    // do this first, so the cart renders with honest prices
    clearStaleDiscountCode();

    Modal.init();
    initProductPickers(document);

    // the cart page has no drawer, so nothing fires afterCartLoad there
    if (confirmationHost()) {
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderUpgradeConfirmations(cart);
          hideInternalProperties();
          hideNamedDiscountLabels();
          relabelUpgradeDiscounts();
          annotateUpgradeLines(cart);
          lockUpgradeQuantity(cart);
          renderDeliveryTotals(cart);
        })
        .catch(function () { /* confirmation is cosmetic */ });
    }

    if (window.jQuery) {
      window.jQuery(document.body).on('afterCartLoad.ajaxCart', function (event, cart) {
        renderCartExtras(cart);
      });
      // quick view injects the product page markup after the fact
      window.jQuery(document.body).on('afterRecommendationLoad', function () {
        initProductPickers(document);
      });
    }

    // quick view / any late DOM insertion. Debounced: the cart drawer and the
    // variant picker both rewrite chunks of the page on every interaction.
    if (window.MutationObserver) {
      var pending = null;
      var observer = new MutationObserver(function () {
        if (pending) return;
        pending = window.setTimeout(function () {
          pending = null;
          initProductPickers(document);
          hideInternalProperties();
          hideNamedDiscountLabels();
          relabelUpgradeDiscounts();
        }, 120);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.packTiersApi = { parseSpec: parseSpec, pricing: pricing, refresh: initProductPickers };
})();
